import { useQueryClient } from "@tanstack/react-query";
import { cloneDeep } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useGetAllScreeningListPrefill } from "@/queries/encounter/screening/use-getAllScreenerAns";
import { useScreeningList } from "@/queries/encounter/screening/use-screening-get-list";
import { useScreeningUpdate } from "@/queries/encounter/screening/use-screening-update";
import {
  DEF_PAGINATION,
  getXtenantId,
  getErrorSnackbar,
  getSuccessSnackbar,
} from "@/utils/utils";
import { PatientScreenerScreenerTypeEnum } from "@/services";
import { DataItem, TableData } from "../AssessmentDrawer";
import {
  SCREENING_EMPTY_ANSWER_MESSAGE,
  SCREENING_MISSING_CONTEXT_MESSAGE,
  SCREENING_PREFILL_PAGE_SIZE,
  SCREENING_PREFILL_QUERY_KEY,
  SCREENING_PREVIEW_QUERY_KEY,
} from "../assessment-constant";
import { ScreenerQuestion } from "../assessment-types";
import { useFreeTextScreener } from "./use-free-text-screener";

export interface UseScreeningAssessmentParams {
  type?: string | null;
  /** Appointment the answers are saved against. */
  appointmentId?: string | null;
  /** Appointment whose saved answers prefill the form; defaults to `appointmentId`. */
  prefillAppointmentId?: string | null;
  patientId?: string;
  enabled?: boolean;
}

export interface UseScreeningAssessment {
  questions: ScreenerQuestion[];
  tableData: TableData | undefined;
  freeText: ReturnType<typeof useFreeTextScreener>;
  isLoading: boolean;
  setRadio: (index: number, value: string) => void;
  /** Answers changed since the last load or save. */
  isDirty: boolean;
  save: (onSaved?: () => void) => Promise<void>;
}

/** Loads a screener's questions and the appointment's saved answers, and saves provider answers. Shared by the Add Screening modal and the inline note sections. */
export const useScreeningAssessment = ({
  type,
  appointmentId,
  prefillAppointmentId = appointmentId,
  patientId,
  enabled = true,
}: UseScreeningAssessmentParams): UseScreeningAssessment => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const [me] = useCurrentUser();
  const { mutateAsync: updateScreeningAns } = useScreeningUpdate();

  const { data, isLoading, isRefetching } = useScreeningList({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    type: type ?? "",
    xTENANTID: getXtenantId(),
    enabled,
  });
  const { data: prefillData, isLoading: isPrefillLoading } =
    useGetAllScreeningListPrefill({
      type: type ?? "",
      pagination: { page: 0, pageSize: SCREENING_PREFILL_PAGE_SIZE },
      gridSorting: [],
      appointmentUuid: prefillAppointmentId ?? undefined,
      xTENANTID: getXtenantId(),
      enabled,
    });

  const questions: ScreenerQuestion[] = data?.data?.screeners ?? [];
  const prefillRows = prefillData?.data?.content;
  const [tableData, setTableData] = useState<TableData | undefined>(data?.data);
  const [savedTableData, setSavedTableData] = useState<TableData | undefined>(
    data?.data
  );

  useEffect(() => {
    const loaded = prefillRows?.length
      ? {
          answerOptions: data?.data?.answerOptions,
          screeners: prefillRows.map((it: DataItem & { answer?: string }) => ({
            ...it,
            answerOptions: it.answer,
          })) as DataItem[],
        }
      : data?.data;
    setTableData(loaded);
    setSavedTableData(loaded);
  }, [data?.data, prefillRows]);

  // Free-text screeners keep their own values; tableData holds prefill rows
  // without question uuids, so the questions come from the screener list.
  const freeText = useFreeTextScreener(
    type,
    data?.data?.screeners,
    prefillRows,
    undefined,
    data?.data?.answerOptions
  );

  const setRadio = useCallback((index: number, value: string) => {
    setTableData((prev) => {
      const next = cloneDeep(prev);
      if (next?.screeners?.[index]) next.screeners[index].answerOptions = value;
      return next;
    });
  }, []);

  const save = useCallback(
    async (onSaved?: () => void) => {
      if (!appointmentId || !patientId) {
        showSnackbar({
          type: "error",
          message: SCREENING_MISSING_CONTEXT_MESSAGE,
        });
        return;
      }
      if (freeText.def && (freeText.isEmpty || freeText.issue)) {
        showSnackbar({
          type: "error",
          message: freeText.issue || SCREENING_EMPTY_ANSWER_MESSAGE,
        });
        return;
      }
      const screenerQuestion = freeText.def
        ? freeText.toRows()
        : (tableData?.screeners?.map((it) => ({
            uuid: it.uuid,
            question: it.question,
            answer: it.answerOptions,
          })) ?? []);
      try {
        const res = await updateScreeningAns({
          patientUuid: patientId,
          xTENANTID: getXtenantId(),
          patientScreener: {
            screenerQuestion,
            appointmentId,
            submittedBy: "PROVIDER",
            screenerType: type as PatientScreenerScreenerTypeEnum,
            providerName: me ? `${me.firstName} ${me.lastName}` : undefined,
            providerRole: me?.role ?? undefined,
          },
        });
        const toast = getSuccessSnackbar(res);
        if (toast?.message) showSnackbar(toast);
        queryClient.invalidateQueries({
          queryKey: [SCREENING_PREFILL_QUERY_KEY],
        });
        queryClient.invalidateQueries({
          queryKey: [SCREENING_PREVIEW_QUERY_KEY],
        });
        onSaved?.();
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
    [
      appointmentId,
      patientId,
      freeText,
      tableData,
      type,
      me,
      updateScreeningAns,
      queryClient,
      showSnackbar,
    ]
  );

  return useMemo(
    () => ({
      questions,
      tableData,
      freeText,
      isLoading: isLoading || isRefetching || isPrefillLoading,
      setRadio,
      isDirty: freeText.def
        ? freeText.isDirty
        : JSON.stringify(tableData) !== JSON.stringify(savedTableData),
      save,
    }),
    [
      questions,
      tableData,
      savedTableData,
      freeText,
      isLoading,
      isRefetching,
      isPrefillLoading,
      setRadio,
      save,
    ]
  );
};
